/**
 * Single source of truth for which document formats the upload surfaces accept.
 *
 * This list must stay in step with the two gates behind it:
 *   - breezeai-backend  src/utils/document-file-type.ts
 *   - breezeai-agents   app/document_extraction/dispatch.py
 * Accepting a format here that either of those rejects turns a clean
 * client-side rejection into a 400 or a failed background job.
 *
 * Every format is matched on MIME *or* extension. Browsers are unreliable
 * about MIME for anything but the common web types — Windows has no registry
 * entry for .md or .txt and sends application/octet-stream (or nothing) for
 * those and for Office files — so extension is a first-class signal here,
 * never a fallback we can skip.
 *
 * Legacy binary Office formats (.doc/.ppt/.xls) are intentionally excluded —
 * Docling cannot read them, and supporting them needs a LibreOffice
 * pre-conversion step in the agents service.
 */

/**
 * Images. Limited to the formats that work on BOTH image paths: Bedrock
 * vision (direct upload) and Docling's _DOCLING_EXTENSIONS (the /extract and
 * Jira flows). GIF and WebP are deliberately absent — Bedrock accepts them but
 * dispatch.py does not, so they would upload on one screen and 422 on another.
 */
export const IMAGE_MIME_TYPES = ["image/jpeg", "image/png"];
export const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png"];

/** Office XML + HTML — extracted by Docling in the agents service. */
export const OFFICE_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/html",
];
export const OFFICE_EXTENSIONS = [".docx", ".pptx", ".xlsx", ".html", ".htm"];

/**
 * The values the backend writes to documents.metadata.fileType for office
 * formats (breezeai-backend src/entities/document.entity.ts FileType).
 * Preview and download surfaces branch on this, not on MIME.
 */
export const OFFICE_FILE_TYPES = ["docx", "pptx", "xlsx", "html"];

/** Read straight off the buffer as UTF-8 by the backend. */
export const TEXT_MIME_TYPES = [
  "text/plain",
  "text/markdown",
  "text/csv",
  "application/json",
];
export const TEXT_EXTENSIONS = [".txt", ".md", ".markdown", ".csv", ".json"];

export const PDF_MIME_TYPES = ["application/pdf"];
export const PDF_EXTENSIONS = [".pdf"];

/** Everything that is not an image, for the surfaces that separate the two. */
export const DOCUMENT_MIME_TYPES = [
  ...PDF_MIME_TYPES,
  ...OFFICE_MIME_TYPES,
  ...TEXT_MIME_TYPES,
];
export const DOCUMENT_EXTENSIONS = [
  ...PDF_EXTENSIONS,
  ...OFFICE_EXTENSIONS,
  ...TEXT_EXTENSIONS,
];

export const ALLOWED_FILE_TYPES = [
  ...IMAGE_MIME_TYPES,
  ...DOCUMENT_MIME_TYPES,
];

export const ALLOWED_EXTENSIONS = [
  ...IMAGE_EXTENSIONS,
  ...DOCUMENT_EXTENSIONS,
];

/** Maximum sizes. The image limit is Bedrock's hard per-image ceiling. */
export const MAX_FILE_SIZE = 50 * 1024 * 1024;
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

/** For interpolating into user-facing copy, so the text can't drift. */
export const MAX_FILE_SIZE_LABEL = "50MB";
export const MAX_IMAGE_SIZE_LABEL = "5MB";

/** Value for an <input type="file"> accept attribute. */
export const FILE_ACCEPT_ATTR = [...ALLOWED_EXTENSIONS, ...ALLOWED_FILE_TYPES].join(",");

/** Human-readable lists for UI copy, so the text can't drift from the rules. */
export const SUPPORTED_FORMATS_LABEL =
  "PDF, Word, PowerPoint, Excel, HTML, images, markdown, CSV, JSON, and text files";
export const SUPPORTED_FORMATS_SHORT =
  "PDF, DOCX, PPTX, XLSX, HTML, TXT, MD, CSV, JSON, JPG, PNG";

export const hasExtension = (fileName: string, extensions: string[]): boolean => {
  const name = fileName.toLowerCase();
  return extensions.some((ext) => name.endsWith(ext));
};

export const isValidFileType = (file: File): boolean =>
  ALLOWED_FILE_TYPES.includes(file.type.toLowerCase()) ||
  hasExtension(file.name, ALLOWED_EXTENSIONS);

export const isImageFile = (file: File): boolean =>
  IMAGE_MIME_TYPES.includes(file.type.toLowerCase()) ||
  hasExtension(file.name, IMAGE_EXTENSIONS);

export const isValidFileSize = (file: File): { valid: boolean; isImage: boolean } => {
  const isImage = isImageFile(file);
  const maxSize = isImage ? MAX_IMAGE_SIZE : MAX_FILE_SIZE;
  return { valid: file.size <= maxSize, isImage };
};
